import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, Divider, FormControl, FormLabel, RadioGroup, Radio,
  FormControlLabel, Autocomplete, Tooltip, Checkbox, Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import HistoryIcon from '@mui/icons-material/History';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ApiIcon from '@mui/icons-material/Api';
import db from '../../data/demoDatabase';
import { apiProductCatalog, PRODUCT_CATEGORIES, daysUntilExpiry } from '../../data/productConfigData';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';

const AGENT_TYPES = [
  { key: 'free_user', label: 'User', shortLabel: 'User', color: '#00B894' },
  { key: 'individual_agent', label: 'Individual Agent', shortLabel: 'Agent', color: '#7B1FA2' },
  { key: 'partner', label: 'Partner', shortLabel: 'Partner', color: '#1B365D' },
];

const statusColors = {
  active: { bg: '#E8F5E9', color: '#2E7D32', label: 'Active' },
  expiring_soon: { bg: '#FFF8E1', color: '#F57F17', label: 'Expiring Soon' },
  expired: { bg: '#FFEBEE', color: '#C62828', label: 'Expired' },
  paused: { bg: '#ECEFF1', color: '#546E7A', label: 'Paused' },
  pending_l1: { bg: '#E3F2FD', color: '#1565C0', label: 'Pending L1' },
  pending_l2: { bg: '#EDE7F6', color: '#4527A0', label: 'Pending L2' },
  rejected: { bg: '#FFEBEE', color: '#C62828', label: 'Rejected' },
};

export default function ProductConfig() {
  const { user } = useAuth();
  const [categoryTab, setCategoryTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [refreshKey, setRefreshKey] = useState(0);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedCommission, setSelectedCommission] = useState(null); // commission being edited (null = add new)

  const bump = () => setRefreshKey((k) => k + 1);

  const allConfigs = useMemo(() => db.productConfigs.getAll(), [refreshKey]);
  const auditLogs = useMemo(() => db.auditLogs.getAll(), [refreshKey]);
  const allUsers = useMemo(() => db.users.getAll(), [refreshKey]);

  const stats = useMemo(() => {
    const active = allConfigs.filter((c) => c.computedStatus === 'active').length;
    const expiring = allConfigs.filter((c) => c.computedStatus === 'expiring_soon').length;
    const pending = allConfigs.filter((c) =>
      c.computedStatus === 'pending_l1' || c.computedStatus === 'pending_l2'
    ).length;
    const expired = allConfigs.filter((c) => c.computedStatus === 'expired').length;
    return { active, expiring, pending, expired };
  }, [allConfigs]);

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  const writeAuditLog = (action, entityId, description, reason = '') => {
    db.auditLogs.create({
      userId: user?.id || 'SYSTEM',
      userName: user?.name || 'System',
      userRole: user?.role || 'admin',
      action, entityType: 'product_config', entityId, description, reason,
    });
  };

  // ─── Import Dialog ──────────────────────────────────────────
  const ImportDialog = () => {
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [duration, setDuration] = useState(12);
    const [reason, setReason] = useState('');

    const existingCodes = allConfigs.map((c) => c.productCode);
    const availableProducts = apiProductCatalog.filter(
      (p) => !existingCodes.includes(p.productCode)
    );

    const toggleSelect = (code) => {
      setSelectedCodes((prev) =>
        prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
      );
    };

    const handleImport = () => {
      if (selectedCodes.length === 0) {
        showSnack('Please select at least one product', 'warning');
        return;
      }
      const now = new Date();
      const expire = new Date(now);
      expire.setMonth(expire.getMonth() + duration);

      selectedCodes.forEach((code) => {
        const apiProduct = apiProductCatalog.find((p) => p.productCode === code);
        if (!apiProduct) return;

        const initialCommission = {
          id: db.generateId('CM'),
          agentType: 'partner',
          specificAgentId: null,
          commissionType: apiProduct.suggestedType,
          perCaseAmount: apiProduct.suggestedPerCase || null,
          tiers: apiProduct.suggestedTiers || [],
          approvalStatus: 'pending_l1',
          l1ApprovedBy: null, l1ApprovedAt: null,
          l2ApprovedBy: null, l2ApprovedAt: null,
          createdBy: user?.id || 'SYSTEM',
          createdAt: new Date().toISOString(),
        };

        const created = db.productConfigs.create({
          productCode: apiProduct.productCode,
          category: apiProduct.category,
          lenderName: apiProduct.lenderName,
          productName: apiProduct.productName,
          contractStartDate: now.toISOString().slice(0, 10),
          contractExpireDate: expire.toISOString().slice(0, 10),
          contractDuration: duration,
          commissions: [initialCommission],
          createdBy: user?.id || 'SYSTEM',
        });

        writeAuditLog(
          'create_product', created.id,
          `Imported ${apiProduct.productName} from imoney.my API`, reason
        );
      });

      showSnack(`${selectedCodes.length} product(s) imported and submitted for approval`);
      setImportDialogOpen(false);
      setSelectedCodes([]);
      setReason('');
      bump();
    };

    return (
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApiIcon sx={{ color: '#1565C0' }} />
          Fetch from imoney.my Product API
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Select products to import from the iMoney product catalog. Imported products enter the 2-layer approval workflow.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <TextField fullWidth select label="Contract Duration *" size="small"
                value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                <MenuItem value={12}>1 Year (12 months)</MenuItem>
                <MenuItem value={24}>2 Years (24 months)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Reason / Justification"
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Q2 expansion, new partnership" />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Available Products ({availableProducts.length})
          </Typography>
          {availableProducts.length === 0 ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              All products from the API catalog have been imported.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedCodes.length === availableProducts.length && availableProducts.length > 0}
                        indeterminate={selectedCodes.length > 0 && selectedCodes.length < availableProducts.length}
                        onChange={(e) =>
                          setSelectedCodes(e.target.checked ? availableProducts.map((p) => p.productCode) : [])
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Lender</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Suggested</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableProducts.map((p) => {
                    const cat = PRODUCT_CATEGORIES.find((c) => c.key === p.category);
                    return (
                      <TableRow key={p.productCode} hover onClick={() => toggleSelect(p.productCode)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedCodes.includes(p.productCode)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{p.productCode}</TableCell>
                        <TableCell>
                          <Chip label={cat?.label || p.category} size="small"
                            sx={{ backgroundColor: `${cat?.color}15`, color: cat?.color, fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{p.lenderName}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{p.productName}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          {p.suggestedType === 'per_case'
                            ? `RM ${p.suggestedPerCase}/case`
                            : `${p.suggestedTiers[0].rate}%+`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setImportDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleImport} disabled={selectedCodes.length === 0}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#1B365D' }}>
            Import {selectedCodes.length > 0 ? `(${selectedCodes.length})` : ''} & Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Commission Dialog ──────────────────────────────────────
  const CommissionDialog = () => {
    const isEditing = !!selectedCommission;
    const [recipientFilter, setRecipientFilter] = useState('partner');
    const [recipient, setRecipient] = useState(null);
    const [commissionType, setCommissionType] = useState('percentage');
    const [perCaseAmount, setPerCaseAmount] = useState('');
    const [tiers, setTiers] = useState([{ min: 0, max: 50000, rate: 1.5 }]);
    const [reason, setReason] = useState('');

    React.useEffect(() => {
      if (!commissionDialogOpen) return;
      if (selectedCommission) {
        // Edit mode — pre-fill from existing commission
        setRecipientFilter(selectedCommission.recipientRole || 'partner');
        const found = allUsers.find((u) => u.id === selectedCommission.recipientId);
        setRecipient(found || null);
        setCommissionType(selectedCommission.commissionType);
        setPerCaseAmount(selectedCommission.perCaseAmount || '');
        setTiers(
          selectedCommission.tiers?.length
            ? selectedCommission.tiers.map((t) => ({ ...t }))
            : [{ min: 0, max: null, rate: 1 }]
        );
      } else {
        // Add new — default values
        setRecipientFilter('partner');
        setRecipient(null);
        setCommissionType('percentage');
        setPerCaseAmount('');
        setTiers([{ min: 0, max: 50000, rate: 1.5 }]);
      }
      setReason('');
    }, [selectedConfig, selectedCommission, commissionDialogOpen]);

    const addTier = () => {
      const lastMax = tiers[tiers.length - 1]?.max || 0;
      setTiers([...tiers, { min: (lastMax || 0) + 1, max: (lastMax || 0) + 100000, rate: 1 }]);
    };
    const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));
    const updateTier = (idx, field, value) => {
      const newTiers = [...tiers];
      newTiers[idx] = { ...newTiers[idx], [field]: value === '' ? null : Number(value) };
      setTiers(newTiers);
    };

    // Filter candidates by role (recipientFilter)
    const eligibleRecipients = allUsers.filter(
      (u) => u.role === recipientFilter && u.status === 'active'
    );

    const handleSave = () => {
      if (!selectedConfig) return;
      if (!recipient) {
        showSnack('Please select a commission recipient', 'warning');
        return;
      }
      if (commissionType === 'per_case' && !perCaseAmount) {
        showSnack('Please enter the per-case amount', 'warning');
        return;
      }

      const payload = {
        recipientId: recipient.id,
        recipientName: recipient.name,
        agentType: recipient.role,
        commissionType,
        perCaseAmount: commissionType === 'per_case' ? Number(perCaseAmount) : null,
        tiers: commissionType === 'percentage' ? tiers : [],
        createdBy: user?.id || 'SYSTEM',
      };

      if (isEditing) {
        // Supersede the old commission
        db.productConfigs.supersedeCommission(
          selectedConfig.id, selectedCommission.id, payload
        );
        writeAuditLog(
          'update_commission', selectedConfig.id,
          `Superseded commission for ${recipient.name} on ${selectedConfig.productName}`,
          reason
        );
        showSnack('New commission submitted — old commission stays active until L2 approval');
      } else {
        db.productConfigs.addCommission(selectedConfig.id, payload);
        writeAuditLog(
          'update_commission', selectedConfig.id,
          `Added commission for ${recipient.name} on ${selectedConfig.productName}`,
          reason
        );
        showSnack('Commission submitted for L1 approval');
      }
      setCommissionDialogOpen(false);
      setSelectedCommission(null);
      bump();
    };

    if (!selectedConfig) return null;

    const todayStr = new Date().toISOString().slice(0, 10);

    return (
      <Dialog open={commissionDialogOpen} onClose={() => { setCommissionDialogOpen(false); setSelectedCommission(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>
            {isEditing ? 'Edit Commission' : 'Add Commission'}
          </Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedConfig.productName} ({selectedConfig.productCode})
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {isEditing && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <strong>Editing: {selectedCommission.recipientName}</strong> — Editing creates a new commission record effective from the approval date.
                  Existing cases created before then will keep the current commission.
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1, display: 'block' }}>
                Recipient Type *
              </FormLabel>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {AGENT_TYPES.map((t) => (
                  <Chip
                    key={t.key} label={t.label}
                    onClick={() => {
                      if (!isEditing) {
                        setRecipientFilter(t.key);
                        setRecipient(null);
                      }
                    }}
                    disabled={isEditing}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: recipientFilter === t.key ? t.color : '#F5F5F5',
                      color: recipientFilter === t.key ? 'white' : '#333',
                      '&:hover': { backgroundColor: recipientFilter === t.key ? t.color : '#E0E0E0' },
                      opacity: isEditing ? 0.6 : 1,
                    }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={eligibleRecipients}
                getOptionLabel={(opt) => `${opt.name} (${opt.id})`}
                value={recipient}
                onChange={(_, val) => setRecipient(val)}
                disabled={isEditing}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Commission Recipient *"
                    size="small"
                    required
                    helperText={isEditing
                      ? 'Recipient is locked when editing an existing commission'
                      : `Select the specific user who will receive this commission (${eligibleRecipients.length} available)`}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl>
                <FormLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Commission Type *</FormLabel>
                <RadioGroup row value={commissionType} onChange={(e) => setCommissionType(e.target.value)}>
                  <FormControlLabel value="per_case" control={<Radio />} label="Per Case (Fixed RM)" />
                  <FormControlLabel value="percentage" control={<Radio />} label="Percentage (of loan amount)" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {commissionType === 'per_case' ? (
              <Grid item xs={6}>
                <TextField fullWidth type="number" label="Amount per Case (RM) *"
                  value={perCaseAmount} onChange={(e) => setPerCaseAmount(e.target.value)}
                  helperText="Paid per approved case" />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Commission Tiers</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Min (RM)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Max (RM)</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Rate (%)</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tiers.map((tier, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <TextField size="small" type="number" value={tier.min ?? ''}
                              onChange={(e) => updateTier(idx, 'min', e.target.value)} sx={{ width: 120 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" placeholder="unlimited" value={tier.max ?? ''}
                              onChange={(e) => updateTier(idx, 'max', e.target.value)} sx={{ width: 120 }} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" type="number" value={tier.rate ?? ''}
                              onChange={(e) => updateTier(idx, 'rate', e.target.value)} sx={{ width: 100 }}
                              InputProps={{ endAdornment: '%' }} />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeTier(idx)} disabled={tiers.length === 1}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button size="small" startIcon={<AddIcon />} onClick={addTier} sx={{ mt: 1, textTransform: 'none' }}>
                  Add Tier
                </Button>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField fullWidth label="Reason / Change Justification" value={reason}
                onChange={(e) => setReason(e.target.value)} multiline rows={2}
                helperText="Required for audit log" />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <strong>Effective from:</strong> {todayStr} (upon L2 approval).
                Cases created <strong>before</strong> the approval date will keep the old commission rate.
                Cases created <strong>on or after</strong> the approval date will use the new rate.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setCommissionDialogOpen(false); setSelectedCommission(null); }} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#1B365D' }}>
            Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Renew Dialog ───────────────────────────────────────────
  const RenewDialog = () => {
    const [newDuration, setNewDuration] = useState(12);
    const [reason, setReason] = useState('');

    const handleRenew = () => {
      if (!selectedConfig) return;
      db.productConfigs.renew(selectedConfig.id, newDuration);
      writeAuditLog(
        'renew', selectedConfig.id,
        `Renewed contract for ${selectedConfig.productName} by ${newDuration} months`, reason
      );
      showSnack('Contract renewed and submitted for approval');
      setRenewDialogOpen(false);
      bump();
    };

    if (!selectedConfig) return null;
    return (
      <Dialog open={renewDialogOpen} onClose={() => setRenewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renew Contract</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Renew contract for <strong>{selectedConfig.productName}</strong>?
          </Typography>
          <TextField fullWidth select label="New Contract Duration"
            value={newDuration} onChange={(e) => setNewDuration(Number(e.target.value))} sx={{ mb: 2 }}>
            <MenuItem value={12}>1 Year (12 months)</MenuItem>
            <MenuItem value={24}>2 Years (24 months)</MenuItem>
          </TextField>
          <TextField fullWidth label="Reason" value={reason}
            onChange={(e) => setReason(e.target.value)} multiline rows={2} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRenewDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleRenew}
            sx={{ borderRadius: 2, textTransform: 'none', backgroundColor: '#00B894' }}>
            Renew Contract
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Audit Dialog ───────────────────────────────────────────
  const AuditDialog = () => {
    if (!selectedConfig) return null;
    const logs = db.auditLogs.getByEntity('product_config', selectedConfig.id);
    return (
      <Dialog open={auditDialogOpen} onClose={() => setAuditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Audit History</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {selectedConfig.productName}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {logs.length === 0 ? (
            <Alert severity="info">No audit entries for this product yet.</Alert>
          ) : (
            logs.map((log) => (
              <Box key={log.id} sx={{ mb: 2, p: 2, border: '1px solid #E0E0E0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Chip label={log.action} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(log.timestamp).toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{log.description}</Typography>
                <Typography variant="caption" color="text.secondary">
                  by {log.userName} ({log.userRole})
                </Typography>
                {log.reason && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Reason: {log.reason}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ─── Product Card ───────────────────────────────────────────
  const ProductCard = ({ config }) => {
    const status = statusColors[config.computedStatus] || statusColors.active;
    const daysLeft = daysUntilExpiry(config.contractExpireDate);
    // Only show commissions that are still current (not superseded) — effectiveTo is null
    const currentCommissions = config.commissions.filter(
      (c) => c.effectiveTo == null
    );
    // Show active ones at the top, then pending ones below
    const sortedCurrent = [...currentCommissions].sort((a, b) => {
      const order = { active: 0, pending_l2: 1, pending_l1: 2, rejected: 3 };
      return (order[a.approvalStatus] ?? 9) - (order[b.approvalStatus] ?? 9);
    });
    const activeCommissions = currentCommissions.filter((c) => c.approvalStatus === 'active');

    const openEdit = (commission) => {
      setSelectedConfig(config);
      setSelectedCommission(commission);
      setCommissionDialogOpen(true);
    };

    const openAdd = () => {
      setSelectedConfig(config);
      setSelectedCommission(null);
      setCommissionDialogOpen(true);
    };

    return (
      <Card sx={{
        borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: (config.computedStatus === 'expired' || config.computedStatus === 'rejected') ? '1px solid #FFCDD2' : '1px solid transparent',
        '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }, transition: 'all 0.2s',
      }}>
        <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#90A4AE' }}>
                {config.productCode}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1B365D', lineHeight: 1.2 }}>
                {config.lenderName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {config.productName}
              </Typography>
            </Box>
            <Chip label={status.label} size="small"
              sx={{ backgroundColor: status.bg, color: status.color, fontWeight: 700, fontSize: '0.65rem', ml: 1 }} />
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Contract Start</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>{config.contractStartDate}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">Expires</Typography>
              <Typography variant="caption" sx={{
                fontWeight: 600,
                color: daysLeft < 0 ? '#C62828' : daysLeft <= 30 ? '#F57F17' : '#2E7D32',
              }}>
                {config.contractExpireDate} ({daysLeft < 0 ? 'expired' : `${daysLeft}d left`})
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1B365D' }}>
              COMMISSIONS ({activeCommissions.length} active)
            </Typography>
            <Tooltip title="Add Commission Recipient">
              <IconButton size="small" onClick={openAdd} sx={{ p: 0.25 }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {sortedCurrent.length === 0 && (
            <Typography variant="caption" color="text.secondary">No commission configured</Typography>
          )}
          {sortedCurrent.slice(0, 5).map((c) => {
            const agent = AGENT_TYPES.find(
              (t) => t.key === c.recipientRole || t.key === c.agentType ||
                (t.key === 'free_user' && c.agentType === 'user')
            );
            const display = c.commissionType === 'per_case'
              ? `RM ${c.perCaseAmount}/case`
              : c.tiers?.length > 0 ? `${c.tiers[0].rate}%${c.tiers.length > 1 ? '+' : ''}` : '-';
            const isPending = c.approvalStatus === 'pending_l1' || c.approvalStatus === 'pending_l2';
            return (
              <Box key={c.id} sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                mb: 0.5, py: 0.5, px: 0.75, borderRadius: 1,
                backgroundColor: isPending ? '#FFF8E1' : 'transparent',
                '&:hover': { backgroundColor: isPending ? '#FFF3C4' : '#F5F7FA' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: agent?.color || '#999', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{
                    fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    color: isPending ? '#F57F17' : '#263238',
                  }}>
                    {c.recipientName || agent?.shortLabel || c.agentType}
                    {isPending && ' ⏳'}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, mx: 1 }}>{display}</Typography>
                <Tooltip title="Edit this commission">
                  <IconButton size="small" onClick={() => openEdit(c)} sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
          {sortedCurrent.length > 5 && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              +{sortedCurrent.length - 5} more...
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Tooltip title="Add Commission">
              <IconButton size="small" onClick={openAdd}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Renew Contract">
              <IconButton size="small" onClick={() => { setSelectedConfig(config); setRenewDialogOpen(true); }}>
                <AutorenewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={config.approvalStatus === 'paused' ? 'Resume' : 'Pause'}>
              <IconButton size="small" onClick={() => {
                db.productConfigs.togglePause(config.id);
                writeAuditLog(
                  config.approvalStatus === 'paused' ? 'resume' : 'pause',
                  config.id,
                  `${config.approvalStatus === 'paused' ? 'Resumed' : 'Paused'} ${config.productName}`
                );
                showSnack('Product status updated');
                bump();
              }}>
                {config.approvalStatus === 'paused'
                  ? <PlayCircleIcon sx={{ fontSize: 16, color: '#00B894' }} />
                  : <PauseCircleIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="View Audit History">
              <IconButton size="small" onClick={() => { setSelectedConfig(config); setAuditDialogOpen(true); }}>
                <HistoryIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // ─── Category tab ────────────────────────────────────────────
  const renderCategoryContent = (categoryKey) => {
    const configs = allConfigs.filter((c) => c.category === categoryKey);
    if (configs.length === 0) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No products in this category. Click "Fetch from imoney.my API" to import.
        </Alert>
      );
    }
    return (
      <Grid container spacing={2.5}>
        {configs.map((config) => (
          <Grid item xs={12} sm={6} md={4} key={config.id}>
            <ProductCard config={config} />
          </Grid>
        ))}
      </Grid>
    );
  };

  // ─── Approval queue ─────────────────────────────────────────
  const renderApprovalQueue = () => {
    const pending = allConfigs.filter(
      (c) => c.computedStatus === 'pending_l1' || c.computedStatus === 'pending_l2'
    );
    if (pending.length === 0) {
      return <Alert severity="success" sx={{ borderRadius: 2 }}>No pending approvals.</Alert>;
    }
    return (
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
              <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Commission</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Submitted By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((config) => {
              const cat = PRODUCT_CATEGORIES.find((c) => c.key === config.category);
              const latest = config.commissions[config.commissions.length - 1];
              const commDisplay = latest?.commissionType === 'per_case'
                ? `RM ${latest.perCaseAmount}/case`
                : latest?.tiers?.[0] ? `${latest.tiers[0].rate}%+` : '-';
              return (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{config.lenderName}</Typography>
                    <Typography variant="caption" color="text.secondary">{config.productName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={cat?.label} size="small"
                      sx={{ backgroundColor: `${cat?.color}15`, color: cat?.color, fontWeight: 600, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{commDisplay}</TableCell>
                  <TableCell>
                    <Chip label={config.computedStatus === 'pending_l1' ? 'Stage 1/2' : 'Stage 2/2'} size="small"
                      sx={{
                        backgroundColor: config.computedStatus === 'pending_l1' ? '#E3F2FD' : '#EDE7F6',
                        color: config.computedStatus === 'pending_l1' ? '#1565C0' : '#4527A0',
                        fontWeight: 700, fontSize: '0.7rem',
                      }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{config.createdBy}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {config.computedStatus === 'pending_l1' && (
                        <Button size="small" variant="contained" startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                          sx={{ backgroundColor: '#00B894', borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                          onClick={() => {
                            db.productConfigs.approveL1(config.id, user?.id || 'SYSTEM');
                            writeAuditLog('approve_l1', config.id, `L1 approval granted for ${config.productName}`);
                            showSnack('L1 approval granted');
                            bump();
                          }}>
                          Approve L1
                        </Button>
                      )}
                      {config.computedStatus === 'pending_l2' && (
                        <Button size="small" variant="contained" startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                          sx={{ backgroundColor: '#1B365D', borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                          onClick={() => {
                            db.productConfigs.approveL2(config.id, user?.id || 'SYSTEM');
                            writeAuditLog('approve_l2', config.id, `L2 approval granted for ${config.productName} — now active`);
                            showSnack('L2 approval granted — product now active');
                            bump();
                          }}>
                          Approve L2
                        </Button>
                      )}
                      <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                        onClick={() => {
                          const reason = window.prompt('Rejection reason?');
                          if (reason) {
                            db.productConfigs.reject(config.id, reason);
                            writeAuditLog('reject', config.id, `Rejected ${config.productName}`, reason);
                            showSnack('Product rejected', 'warning');
                            bump();
                          }
                        }}>
                        Reject
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ─── Audit log table ────────────────────────────────────────
  const renderAuditLog = () => {
    const columns = [
      { field: 'timestamp', headerName: 'Timestamp', renderCell: (row) => (
        <Typography variant="caption">{new Date(row.timestamp).toLocaleString()}</Typography>
      )},
      { field: 'action', headerName: 'Action', renderCell: (row) => (
        <Chip label={row.action} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
      )},
      { field: 'entityId', headerName: 'Entity' },
      { field: 'userName', headerName: 'User' },
      { field: 'userRole', headerName: 'Role' },
      { field: 'description', headerName: 'Description' },
      { field: 'reason', headerName: 'Reason' },
    ];
    return <DataTable columns={columns} rows={auditLogs} title="Audit Log" exportable pageSize={15} />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1B365D' }}>
            Product & Commission Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unified product lifecycle and commission management with 2-layer approval
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<ApiIcon />} onClick={() => setImportDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#1B365D' }}>
            Fetch from imoney.my API
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={bump}
            sx={{ borderRadius: 2, textTransform: 'none' }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Active Products" value={stats.active} subtitle="currently live"
            icon={CheckCircleIcon} color="#00B894" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Expiring Soon" value={stats.expiring} subtitle="within 30 days"
            icon={WarningIcon} color="#F57F17" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Pending Approval" value={stats.pending} subtitle="awaiting review"
            icon={ScheduleIcon} color="#1565C0" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Expired" value={stats.expired} subtitle="contract lapsed"
            icon={CancelIcon} color="#C62828" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <Tabs value={categoryTab} onChange={(_, v) => setCategoryTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #E0E0E0', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          {PRODUCT_CATEGORIES.map((cat) => (
            <Tab key={cat.key} label={cat.label} />
          ))}
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Approval Queue
              {stats.pending > 0 && (
                <Chip label={stats.pending} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#C62828', color: 'white', fontWeight: 700 }} />
              )}
            </Box>
          } />
          <Tab label="Audit Log" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {categoryTab < PRODUCT_CATEGORIES.length && renderCategoryContent(PRODUCT_CATEGORIES[categoryTab].key)}
          {categoryTab === PRODUCT_CATEGORIES.length && renderApprovalQueue()}
          {categoryTab === PRODUCT_CATEGORIES.length + 1 && renderAuditLog()}
        </Box>
      </Paper>

      {/* Dialogs */}
      <ImportDialog />
      <CommissionDialog />
      <RenewDialog />
      <AuditDialog />

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}
          sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
